/**
 * Link Point Cloud Tool
 *
 * Links laser-scan point data. Textual scan formats (.xyz, .pts, .asc, ASCII
 * .ply, .csv) are actually parsed into a renderable vertex buffer; binary
 * containers (.rcp/.rcs/.e57/.las) are registered as references with a clear
 * warning, exactly like unsupported-format messaging in BIM.
 */

import { v4 as uuidv4 } from 'uuid';
import { LinkElement, LinkGeometryData } from '../core/types';
import { BaseTool } from './BaseTool';
import { LinkManager } from '../core/LinkManager';
import { ThreeEvent } from '@react-three/fiber';
import { parsePointCloudFile, isParsablePointCloud, PointCloudData } from '../services/pointcloud/PointCloudParser';
import i18n from '../i18n';

export class LinkPointCloudTool extends BaseTool {
    id = 'link_point_cloud';
    name = 'Link Point Cloud';
    cursor = 'alias';

    private linkManager: LinkManager;

    constructor(linkManager: LinkManager) {
        super();
        this.linkManager = linkManager;
    }

    public executeLink(filePath: string, size = 0) {
        // 1. Create Link Instance
        const linkInstance = this.linkManager.linkFile('PointCloud', filePath, {
            positioning: 'OriginToOrigin',
            size
        });

        // 2. Create Wrapper Element
        const element = this.createLinkElement(linkInstance);
        this.linkManager.setDocumentId(linkInstance.id, element.id);
        return linkInstance;
    }

    onActivate(): void {
        // Dialog-driven command: BIM opens a file dialog for point clouds.
        void this.pickFile('.rcp,.rcs,.e57,.las,.xyz,.pts,.asc,.ply,.csv').then(async (file) => {
            if (file) {
                const link = this.executeLink(file.name, file.size);
                if (isParsablePointCloud(file.name)) {
                    await this.attachPoints(link.id, file);
                } else {
                    this.notify(i18n.t('tools.notify.pointCloudLinked', { fileName: file.name }),
                        'warning'
                    );
                }
            }
            this.exitToSelect();
        });
    }

    /** Parse a textual scan file and patch the vertex payload into the element. */
    private async attachPoints(linkId: string, file: File): Promise<void> {
        if (!this.context) return;
        try {
            const data: PointCloudData = await parsePointCloudFile(file);
            const elements = Array.from(this.context.bimStore.project.elements.values());
            const el = elements.find(e => (e.geometryData as LinkGeometryData | undefined)?.linkId === linkId);
            if (!el) return;
            const [minX, minY, minZ, maxX, maxY, maxZ] = data.bounds;
            const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2, cz = (minZ + maxZ) / 2;
            this.context.bimStore.updateElement(el.id, {
                geometryData: {
                    ...(el.geometryData as LinkGeometryData),
                    // Vertices are re-centered around the bbox so the cloud
                    // sits at the object origin like every other link.
                    points: Array.from(data.positions, (v, i) => v - [cx, cy, cz][i % 3] as number),
                    colors: Array.from(data.colors),
                    origin: [0, 0, 0],
                    boxSize: [
                        Math.max(maxX - minX, 1),
                        Math.max(maxY - minY, 1),
                        Math.max(maxZ - minZ, 1),
                    ] as [number, number, number],
                },
                parameters: {
                    ...el.parameters,
                    'Point Count': String(data.count),
                },
            } as any);
            this.notify(i18n.t('tools.notify.pointCloudParsed', { fileName: file.name, count: data.count }), 'success');
        } catch (err) {
            this.linkManager.setStatus(linkId, 'Outdated');
            this.notify(i18n.t('tools.notify.pointCloudUnreadable', { message: (err as Error).message }), 'warning');
        }
    }

    onPointerDown(_event: ThreeEvent<PointerEvent>): void {
        // Dialog-driven command: nothing to place interactively.
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void { }
    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private createLinkElement(link: any) {
        const geometryData: LinkGeometryData = {
            linkId: link.id,
            origin: [0, 0, 0],
            scale: [1, 1, 1],
            rotation: [0, 0, 0],
            boxSize: [50, 50, 10]
        };

        const linkElement: LinkElement = {
            id: uuidv4(),
            category: 'Point Cloud',
            type: 'Linked Scan: ' + link.name,
            parameters: {
                'File Path': link.path,
                'Scale': '1.0',
                'Color Mode': 'RGB'
            },
            constraints: [],
            geometryData
        };

        this.context?.bimStore.addElement(linkElement);
        console.log(`Point Cloud Link Created: ${linkElement.id}`);
        return linkElement;
    }
}
